import {
  CALCULATION_NORMS,
  type CalculationKind,
  type CalculationRecord
} from "../../domain/engineering/calculation-record.js";
import type { Api570RateBasis, Api570Result } from "../../domain/engineering/api570-calculator.js";
import type { NaceResult } from "../../domain/engineering/nace-calculator.js";
import type { AsmeB313Result } from "../../domain/engineering/asme-b313-calculator.js";
import type { CmlSummary, CmlThicknessHistory } from "../../domain/engineering/me-thickness-history.js";
import { INSUFFICIENT_HISTORY_MESSAGE } from "../../domain/engineering/me-thickness-history.js";
import type { CouponMaterial, PipingMaterial, NormEdition, CatalogLookup } from "../../domain/engineering/integrity-catalog.js";
import { formatMaterialLabel } from "../../domain/engineering/integrity-catalog.js";
import type { CalculableLine, LineCalculationContext, Api570PointContext } from "../../application/services/engineering-calculation-service.js";
import { formatDecimalBR, parseDecimalBR } from "../../domain/engineering/me-measurements.js";
import { contextStore } from "../auth/context-store.js";

/**
 * CÁLCULOS DE INTEGRIDADE — três calculadoras independentes, cada uma na sua
 * tela:
 *
 *   ASME B31.3 → espessura mínima requerida
 *   API 570    → taxa de corrosão + vida remanescente
 *   NACE/AMPP  → taxa média de corrosão por cupom
 *
 * A simplificação está em NÃO pedir o que o sistema já sabe: os dados de
 * projeto vêm do cadastro da linha, S/E/W/Y vêm dos catálogos normativos, o
 * histórico de espessura vem dos relatórios de ME e a densidade do cupom vem
 * do catálogo de materiais. O usuário confere e decide.
 *
 * Os três motores continuam separados: nenhum botão roda mais de um cálculo e
 * o resultado NACE nunca alimenta o API 570.
 */

type View = "HUB" | "ASME_B313" | "API570" | "NACE";

const TAB_SHORT_LABELS: Record<CalculationKind, string> = {
  API570: "API 570",
  NACE: "NACE / AMPP SP0775",
  ASME_B313: "ASME B31.3"
};

const RATE_BASIS_LABELS: Record<Api570RateBasis, string> = {
  HIGHEST: "Maior taxa (referência conservadora)",
  SHORT_TERM: "Curto prazo",
  LONG_TERM: "Longo prazo",
  MANUAL: "Outra taxa tecnicamente definida"
};

/** Formatos da interface brasileira. */
const fmt = (v: number | null | undefined, decimals: number, unit = "") =>
  v === null || v === undefined || !Number.isFinite(v) ? "—" : `${formatDecimalBR(v, decimals)}${unit ? ` ${unit}` : ""}`;
const fmtThk = (v: number | null | undefined) => fmt(v, 2, "mm");
const fmtRate = (v: number | null | undefined) => fmt(v, 4, "mm/ano");
const fmtYears = (v: number | null | undefined) => fmt(v, 2, "anos");
const fmtMass = (v: number | null | undefined) => fmt(v, 4, "g");
const fmtArea = (v: number | null | undefined) => fmt(v, 2, "mm²");

function todayIso(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}

/** "yyyy-mm-dd" → "dd/mm/yyyy" sem passar por Date(), para não deslocar o dia. */
function fmtCivilDate(value: string | null | undefined): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec((value ?? "").trim());
  return match ? `${match[3]}/${match[2]}/${match[1]}` : "—";
}

function fmtTimestamp(iso: string): string {
  const date = new Date(iso);
  if (isNaN(date.getTime())) return iso;
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

interface AsmeScenario {
  lineId: string;
  materialId: string;
  editionId: string;
  pressure: string;
  temperature: string;
  corrosionAllowance: string;
  erosionAllowance: string;
  mechanicalAllowance: string;
  negativeTolerance: string;
  manualStress: string;
  manualQualityFactor: string;
  manualWeldFactor: string;
  manualYCoefficient: string;
  manualJustification: string;
  notes: string;
}

interface Api570Scenario {
  lineId: string;
  cmlId: string;
  rateBasis: Api570RateBasis;
  manualRate: string;
  rateJustification: string;
  manualMinimumThickness: string;
  minimumJustification: string;
  notes: string;
}

interface NaceScenario {
  couponMaterialId: string;
  length: string;
  width: string;
  thickness: string;
  holeDiameter: string;
  maskedArea: string;
  areaOverride: string;
  areaJustification: string;
  initialMass: string;
  finalMass: string;
  installationDate: string;
  removalDate: string;
  equipmentTag: string;
  notes: string;
  showOtherUnits: boolean;
}

export class EngineeringCalculationsPage extends HTMLElement {
  private p: any;
  private ctx: any;

  private view: View = "HUB";
  private loading = false;

  private lines: CalculableLine[] = [];
  /** Cliente/Projeto apenas ESTREITAM a lista de TAGs; nunca a zeram. */
  private clients: Array<{ id: string; name: string }> = [];
  private projects: Array<{ id: string; code: string; name: string; clientId: string }> = [];
  private scope = { clientId: "", projectId: "" };
  private linesError: string | null = null;
  private materials: PipingMaterial[] = [];
  private couponMaterials: CouponMaterial[] = [];
  private asmeEditions: NormEdition[] = [];

  private lineContext: LineCalculationContext | null = null;
  private cmls: CmlSummary[] = [];
  private pointContext: Api570PointContext | null = null;

  private asme: AsmeScenario = {
    lineId: "", materialId: "", editionId: "", pressure: "", temperature: "",
    corrosionAllowance: "", erosionAllowance: "", mechanicalAllowance: "",
    negativeTolerance: "12,50", manualStress: "", manualQualityFactor: "",
    manualWeldFactor: "", manualYCoefficient: "", manualJustification: "", notes: ""
  };

  private api: Api570Scenario = {
    lineId: "", cmlId: "", rateBasis: "HIGHEST", manualRate: "",
    rateJustification: "", manualMinimumThickness: "", minimumJustification: "", notes: ""
  };

  private nace: NaceScenario = {
    couponMaterialId: "", length: "", width: "", thickness: "", holeDiameter: "",
    maskedArea: "", areaOverride: "", areaJustification: "", initialMass: "",
    finalMass: "", installationDate: "", removalDate: todayIso(), equipmentTag: "",
    notes: "", showOtherUnits: false
  };

  private asmeResult: { result: AsmeB313Result; code: string | null } | null = null;
  private apiResult: { result: Api570Result; code: string | null } | null = null;
  private naceResult: { result: NaceResult; code: string | null } | null = null;

  private history: Record<CalculationKind, CalculationRecord[]> = { API570: [], NACE: [], ASME_B313: [] };
  private errors: Record<CalculationKind, string | null> = { API570: null, NACE: null, ASME_B313: null };

  async connectedCallback() {
    this.p = (window as any).app?.platform;
    const snapshot = contextStore.get();
    this.ctx = {
      activeClientId: snapshot.activeClientId,
      activeClientName: snapshot.activeClientName,
      userId: snapshot.userId,
      userRole: snapshot.role,
      permissions: snapshot.permissions
    };
    this.render();
    await this.loadCatalogs();
  }

  private get service() { return this.p?.engineeringCalculationService; }
  private get catalogService() { return this.p?.integrityCatalogService; }

  /**
   * Recarrega as TAGs a partir do cadastro de equipamentos.
   *
   * Fonte única: EngineeringCalculationService.listCalculableLines, que lê o
   * EquipmentRepository real. Não existe lista local nem catálogo paralelo de
   * linhas nesta página.
   */
  private async reloadLines() {
    this.linesError = null;
    try {
      this.lines = await this.service.listCalculableLines(this.ctx, {
        ...(this.scope.clientId ? { clientId: this.scope.clientId } : {}),
        ...(this.scope.projectId ? { projectId: this.scope.projectId } : {})
      });
    } catch (e: any) {
      // Uma lista vazia por erro não pode passar por "não há linhas".
      console.error("[CÁLCULOS] Falha ao listar as tubulações cadastradas", e);
      this.linesError = e?.safeMessage || e?.message || "Não foi possível carregar as tubulações cadastradas.";
      this.lines = [];
    }
  }

  private async loadCatalogs() {
    if (!this.service) { this.render(); return; }
    try {
      // O cliente ativo apenas pré-seleciona o escopo; a lista não depende dele.
      this.scope.clientId = this.ctx.activeClientId || "";

      if (this.p?.adminService) {
        const clients = await this.p.adminService.listAuthorizedClients(this.ctx).catch(() => []);
        this.clients = clients.map((c: any) => ({ id: c.id, name: c.name || c.legalName }));
      }
      if (this.p?.projectService) {
        const projects = await this.p.projectService.listProjects(undefined, undefined, this.ctx).catch(() => []);
        this.projects = projects.map((p: any) => ({ id: p.id, code: p.code, name: p.name, clientId: p.clientId }));
      }

      await this.reloadLines();
      if (this.catalogService) {
        this.materials = await this.catalogService.listMaterials(this.ctx);
        this.couponMaterials = await this.catalogService.listCouponMaterials(this.ctx);
        this.asmeEditions = await this.catalogService.listEditions(this.ctx, "ASME B31.3");
        const def = this.asmeEditions.find(e => e.isDefault) ?? this.asmeEditions[0];
        if (def && !this.asme.editionId) this.asme.editionId = def.id;
      }
      await this.reloadHistory();
    } catch (e) {
      console.error("[CÁLCULOS] Falha ao carregar catálogos", e);
    }
    this.render();
  }

  private async reloadHistory() {
    for (const kind of ["API570", "NACE", "ASME_B313"] as CalculationKind[]) {
      try {
        this.history[kind] = await this.service.listHistory(this.ctx, kind);
      } catch {
        this.history[kind] = [];
      }
    }
  }

  private esc(value: unknown): string {
    return String(value ?? "").replace(/[&<>"']/g, c =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] || c)
    );
  }

  private val(id: string): string {
    const el = this.querySelector<HTMLInputElement | HTMLSelectElement>(`#${id}`);
    return el ? el.value : "";
  }

  private message(text: string): string {
    return `<div class="calc-error">${this.esc(text)}</div>`;
  }

  /** Valor normativo com a sua origem — nunca um número solto na tela. */
  private renderLookup(label: string, lookup: CatalogLookup<number> | null, decimals: number, unit: string): string {
    if (!lookup) {
      return `
        <div class="res res-missing">
          <span>${this.esc(label)}</span>
          <strong>—</strong>
          <small>Não resolvido pelo catálogo</small>
        </div>`;
    }
    const pending = lookup.verification === "PENDING_VALIDATION";
    return `
      <div class="res">
        <span>${this.esc(label)}</span>
        <strong>${fmt(lookup.value, decimals, unit)}</strong>
        <small>
          ${this.esc(lookup.source)}
          ${lookup.interpolated ? " · interpolado entre linhas da tabela" : ""}
          ${pending ? `<b class="pending">CONFERIR NA EDIÇÃO ADOTADA</b>` : ""}
        </small>
      </div>`;
  }

  // =========================================================================
  // HUB
  // =========================================================================

  private renderHub(): string {
    const card = (kind: CalculationKind, title: string, subtitle: string, view: View) => `
      <div class="hub-card">
        <div class="hub-norm">${this.esc(CALCULATION_NORMS[kind].norm)}</div>
        <h2>${this.esc(title)}</h2>
        <p>${this.esc(subtitle)}</p>
        <div class="hub-count">${this.history[kind].length} cálculo(s) no histórico</div>
        <button type="button" class="btn-calc btn-open" data-view="${view}">ABRIR CALCULADORA</button>
      </div>`;

    return `
      <div class="hub-grid">
        ${card("ASME_B313", "ASME B31.3", "Espessura mínima requerida", "ASME_B313")}
        ${card("API570", "API 570", "Corrosão / Vida Remanescente", "API570")}
        ${card("NACE", "NACE / AMPP SP0775", "Cupom de Corrosão", "NACE")}
      </div>`;
  }

  // =========================================================================
  // ASME B31.3
  // =========================================================================

  private captureAsme() {
    this.asme = {
      ...this.asme,
      materialId: this.val("asme-material") || this.asme.materialId,
      editionId: this.val("asme-edition") || this.asme.editionId,
      pressure: this.val("asme-pressure"),
      temperature: this.val("asme-temperature"),
      corrosionAllowance: this.val("asme-ca"),
      erosionAllowance: this.val("asme-ea"),
      mechanicalAllowance: this.val("asme-ma"),
      negativeTolerance: this.val("asme-tol"),
      manualStress: this.val("asme-manual-s"),
      manualQualityFactor: this.val("asme-manual-e"),
      manualWeldFactor: this.val("asme-manual-w"),
      manualYCoefficient: this.val("asme-manual-y"),
      manualJustification: this.val("asme-manual-just"),
      notes: this.val("asme-notes")
    };
  }

  /** Recarrega os dados da linha (e o S da temperatura do cenário) sem tocar no cadastro. */
  private async reloadLineContext() {
    this.errors.ASME_B313 = null;
    if (!this.asme.lineId) { this.lineContext = null; this.render(); return; }

    this.loading = true;
    this.render();
    try {
      const temperature = parseDecimalBR(this.asme.temperature);
      this.lineContext = await this.service.loadLineContext(this.ctx, this.asme.lineId, {
        editionId: this.asme.editionId,
        materialId: this.asme.materialId || null,
        temperatureOverrideCelsius: temperature
      });

      const design = this.lineContext!.design;
      if (!this.asme.materialId && this.lineContext!.matchedMaterial) {
        this.asme.materialId = this.lineContext!.matchedMaterial.id;
      }
      // Pré-preenchimento vindo do cadastro — o usuário não redigita.
      if (!this.asme.pressure && design.designPressureMPa.value !== null) {
        this.asme.pressure = formatDecimalBR(design.designPressureMPa.value, 2);
      }
      if (!this.asme.temperature && design.designTemperatureCelsius.value !== null) {
        this.asme.temperature = formatDecimalBR(design.designTemperatureCelsius.value, 2);
      }
      if (!this.asme.corrosionAllowance && design.corrosionAllowanceMm.value !== null) {
        this.asme.corrosionAllowance = formatDecimalBR(design.corrosionAllowanceMm.value, 2);
      }
    } catch (e: any) {
      console.error("[CÁLCULOS][ASME] Falha ao carregar dados da linha", e);
      this.errors.ASME_B313 = e?.safeMessage || e?.message || "Não foi possível carregar os dados da linha.";
      this.lineContext = null;
    }
    this.loading = false;
    this.render();
  }

  /** Parâmetros efetivamente usados: tabela normativa ou entrada manual justificada. */
  private resolveAsmeFactors() {
    const n = this.lineContext?.normative ?? null;
    const pick = (lookup: CatalogLookup<number> | null | undefined, manual: string) => {
      if (lookup) return { value: lookup.value, manual: false, source: lookup.source };
      const parsed = parseDecimalBR(manual);
      return { value: parsed, manual: true, source: "Entrada manual justificada" };
    };
    return {
      S: pick(n?.allowableStress, this.asme.manualStress),
      E: pick(n?.qualityFactor, this.asme.manualQualityFactor),
      W: pick(n?.weldJointFactor, this.asme.manualWeldFactor),
      Y: pick(n?.yCoefficient, this.asme.manualYCoefficient)
    };
  }

  private buildAsmeInputs() {
    const design = this.lineContext!.design;
    const factors = this.resolveAsmeFactors();
    return {
      designPressure: parseDecimalBR(this.asme.pressure),
      outsideDiameter: design.outsideDiameterMm.value,
      allowableStress: factors.S.value,
      qualityFactor: factors.E.value,
      weldJointFactor: factors.W.value,
      yCoefficient: factors.Y.value,
      corrosionAllowance: parseDecimalBR(this.asme.corrosionAllowance),
      erosionAllowance: parseDecimalBR(this.asme.erosionAllowance),
      mechanicalAllowance: parseDecimalBR(this.asme.mechanicalAllowance),
      negativeTolerancePercent: parseDecimalBR(this.asme.negativeTolerance),
      material: this.lineContext!.matchedMaterial ? formatMaterialLabel(this.lineContext!.matchedMaterial) : (design.material || ""),
      designTemperatureCelsius: parseDecimalBR(this.asme.temperature),
      pressureUnit: "MPa",
      lengthUnit: "mm"
    };
  }

  /** Bloqueio da regra "não inventar S": sem S resolvido nem manual, não calcula. */
  private asmeBlockingReason(): string | null {
    if (!this.lineContext) return "Selecione a TAG da linha.";
    const design = this.lineContext.design;
    if (design.outsideDiameterMm.value === null) {
      return "Diâmetro externo não cadastrado para esta linha. Cadastre o diâmetro (ou NPS/DN) antes de calcular.";
    }
    const factors = this.resolveAsmeFactors();
    if (factors.S.value === null) {
      return this.lineContext.normative?.allowableStressMessage
        ?? "Tensão admissível não encontrada para o material/temperatura selecionados.";
    }
    const manualUsed = [factors.S, factors.E, factors.W, factors.Y].some(f => f.manual && f.value !== null);
    if (manualUsed && !this.asme.manualJustification.trim()) {
      return "Parâmetro normativo informado manualmente: registre a justificativa técnica.";
    }
    for (const [label, factor] of [["E", factors.E], ["W", factors.W], ["Y", factors.Y]] as const) {
      if (factor.value === null) return `Fator ${label} não resolvido: informe o valor e a justificativa técnica.`;
    }
    return null;
  }

  private async calculateAsme(persist: boolean) {
    this.captureAsme();
    this.errors.ASME_B313 = null;

    const blocked = this.asmeBlockingReason();
    if (blocked) {
      this.errors.ASME_B313 = blocked;
      this.asmeResult = null;
      this.render();
      return;
    }

    try {
      const inputs = this.buildAsmeInputs();
      const design = this.lineContext!.design;
      const factors = this.resolveAsmeFactors();

      if (!persist) {
        this.asmeResult = { result: this.service.previewAsmeB313(this.ctx, inputs), code: null };
      } else {
        const edition = this.asmeEditions.find(e => e.id === this.asme.editionId);
        const { result, record } = await this.service.runAsmeB313(this.ctx, inputs, {
          equipmentId: design.equipmentId,
          equipmentTag: design.tag,
          notes: this.asme.notes,
          editionId: this.asme.editionId,
          editionLabel: edition ? `${edition.norm} ${edition.edition}` : undefined,
          provenance: {
            diametroExterno: design.outsideDiameterMm.sourceLabel,
            pressao: design.designPressureMPa.sourceLabel,
            temperatura: design.designTemperatureCelsius.sourceLabel,
            sobreespessuraCorrosao: design.corrosionAllowanceMm.sourceLabel,
            tensaoAdmissivel: factors.S.source,
            fatorE: factors.E.source,
            fatorW: factors.W.source,
            coeficienteY: factors.Y.source,
            justificativaManual: this.asme.manualJustification || "—"
          }
        });
        this.asmeResult = { result, code: record.code };
        await this.reloadHistory();
      }
    } catch (e: any) {
      console.error("[CÁLCULOS][ASME] Falha ao calcular", e);
      this.errors.ASME_B313 = e?.safeMessage || e?.message || "Não foi possível executar o cálculo ASME B31.3.";
      this.asmeResult = null;
    }
    this.render();
  }

  private renderAsmeScreen(): string {
    const design = this.lineContext?.design ?? null;
    const normative = this.lineContext?.normative ?? null;
    const r = this.asmeResult?.result;
    const registeredTemp = design?.designTemperatureCelsius.value ?? null;
    const usedTemp = parseDecimalBR(this.asme.temperature);
    const registeredPressure = design?.designPressureMPa.value ?? null;
    const usedPressure = parseDecimalBR(this.asme.pressure);
    const isTempScenario = registeredTemp !== null && usedTemp !== null && Math.abs(registeredTemp - usedTemp) > 1e-9;
    const isPressureScenario = registeredPressure !== null && usedPressure !== null && Math.abs(registeredPressure - usedPressure) > 1e-9;

    return `
      <div class="calc-panel">
        ${this.renderScreenHeader("ASME_B313", "Espessura Mínima de Parede", "Tubo reto sob pressão interna.")}

        <div class="calc-subtitle">1 — Linha (cadastro de Equipamentos &amp; Ativos)</div>
        ${this.renderScopeFilters("asme")}
        <div class="calc-grid" style="margin-top:12px;">
          <div class="fld fld-wide">
            <label>TAG da Linha *</label>
            <select id="asme-line">
              <option value="">Selecionar linha cadastrada…</option>
              ${this.lines.map(l => `<option value="${this.esc(l.equipmentId)}" ${this.asme.lineId === l.equipmentId ? "selected" : ""}>${this.esc(this.lineLabel(l))}</option>`).join("")}
            </select>
            ${this.renderLinesNotice()}
          </div>
          <div class="fld">
            <label>Edição ASME B31.3</label>
            <select id="asme-edition">
              ${this.asmeEditions.map(e => `<option value="${this.esc(e.id)}" ${this.asme.editionId === e.id ? "selected" : ""}>${this.esc(e.norm)} ${this.esc(e.edition)}</option>`).join("")}
            </select>
          </div>
        </div>

        ${this.loading ? `<div class="loading">Carregando dados da linha…</div>` : ""}

        ${design ? `
          ${this.renderRegistryGaps(design, this.lineContext?.matchedMaterial ?? null)}
          <div class="calc-subtitle">2 — Dados do cadastro da linha</div>
          <div class="registry-grid">
            ${this.registryField("Cliente", this.ctx.activeClientName || "—")}
            ${this.registryField("TAG", design.tag)}
            ${this.registryField("Serviço / Fluido", [design.service, design.fluid].filter(Boolean).join(" / ") || "—")}
            ${this.registryField("Classe da linha", design.pressureClass || design.fluidClass || "—")}
            ${this.registryField("NPS", design.nps ? `${design.nps}"` : "—")}
            ${this.registryField("DN", design.dn !== null ? String(design.dn) : "—")}
            ${this.registryField("Diâmetro externo", fmtThk(design.outsideDiameterMm.value), design.outsideDiameterMm.sourceLabel)}
            ${this.registryField("Schedule", design.schedule || "—")}
            ${this.registryField("Espessura nominal", fmtThk(design.nominalThicknessMm.value), design.nominalThicknessMm.sourceLabel)}
            ${this.registryField("Material (cadastro)", design.material || "—")}
            ${this.registryField("Especificação / Grau", [design.materialSpecification, design.materialGrade].filter(Boolean).join(" ") || "—")}
            ${this.registryField("Pressão de projeto", fmt(design.designPressureMPa.value, 2, "MPa"), design.designPressureMPa.sourceLabel)}
            ${this.registryField("Temperatura de projeto", fmt(design.designTemperatureCelsius.value, 2, "°C"), design.designTemperatureCelsius.sourceLabel)}
            ${this.registryField("Corrosion Allowance", fmtThk(design.corrosionAllowanceMm.value), design.corrosionAllowanceMm.sourceLabel)}
            ${this.registryField("Norma de fabricação", design.fabricationStandard || "—")}
          </div>

          <div class="calc-subtitle">3 — Material</div>
          <div class="calc-grid">
            <div class="fld fld-wide">
              <label>Material *</label>
              <select id="asme-material">
                <option value="">Selecionar…</option>
                ${this.materials.map(m => `<option value="${this.esc(m.id)}" ${this.asme.materialId === m.id ? "selected" : ""}>${this.esc(formatMaterialLabel(m))}</option>`).join("")}
              </select>
              ${this.lineContext?.matchedMaterial
                ? `<span class="fld-trace">Pré-selecionado a partir do cadastro da linha.</span>`
                : `<span class="fld-hint">O cadastro da linha não casou com nenhum material do catálogo — selecione.</span>`}
            </div>
          </div>

          <div class="calc-subtitle">4 — Cenário de cálculo</div>
          <div class="calc-grid">
            <div class="fld">
              <label>Pressão de projeto (MPa)</label>
              <input id="asme-pressure" inputmode="decimal" value="${this.esc(this.asme.pressure)}" placeholder="0,00" />
              ${isPressureScenario
                ? `<span class="fld-scenario">Cadastrado: ${fmt(registeredPressure, 2, "MPa")} · Utilizado no cálculo: ${fmt(usedPressure, 2, "MPa")}</span>`
                : `<span class="fld-hint">Valor do cadastro. Alterar aqui cria um cenário e não altera o cadastro.</span>`}
            </div>
            <div class="fld">
              <label>Temperatura de projeto (°C)</label>
              <input id="asme-temperature" inputmode="decimal" value="${this.esc(this.asme.temperature)}" placeholder="0,00" />
              ${isTempScenario
                ? `<span class="fld-scenario">Cadastrado: ${fmt(registeredTemp, 2, "°C")} · Utilizado no cálculo: ${fmt(usedTemp, 2, "°C")}</span>`
                : `<span class="fld-hint">Alterar a temperatura busca a tensão admissível correspondente.</span>`}
            </div>
            <div class="fld"><label>Sobreespessura de corrosão (mm)</label><input id="asme-ca" inputmode="decimal" value="${this.esc(this.asme.corrosionAllowance)}" placeholder="0,00" /></div>
            <div class="fld"><label>Sobreespessura de erosão (mm)</label><input id="asme-ea" inputmode="decimal" value="${this.esc(this.asme.erosionAllowance)}" placeholder="0,00" /></div>
            <div class="fld"><label>Sobreespessura mecânica (mm)</label><input id="asme-ma" inputmode="decimal" value="${this.esc(this.asme.mechanicalAllowance)}" placeholder="0,00" /></div>
            <div class="fld"><label>Tolerância negativa de fabricação (%)</label><input id="asme-tol" inputmode="decimal" value="${this.esc(this.asme.negativeTolerance)}" placeholder="12,50" /></div>
          </div>

          <div class="calc-subtitle">5 — Parâmetros normativos (determinados pelo catálogo)</div>
          <div class="result-grid">
            ${this.renderLookup("Tensão admissível S", normative?.allowableStress ?? null, 2, "MPa")}
            ${this.renderLookup("Fator de qualidade E", normative?.qualityFactor ?? null, 2, "")}
            ${this.renderLookup("Fator da junta soldada W", normative?.weldJointFactor ?? null, 2, "")}
            ${this.renderLookup("Coeficiente Y", normative?.yCoefficient ?? null, 2, "")}
          </div>
          ${normative?.allowableStressMessage ? this.message(normative.allowableStressMessage) : ""}

          ${(normative?.unresolved.length ?? 0) > 0 ? `
            <div class="manual-box">
              <div class="manual-title">Entrada manual — somente para o que o catálogo não resolveu</div>
              <div class="calc-grid">
                ${!normative?.allowableStress ? `<div class="fld"><label>S manual (MPa)</label><input id="asme-manual-s" inputmode="decimal" value="${this.esc(this.asme.manualStress)}" /></div>` : ""}
                ${!normative?.qualityFactor ? `<div class="fld"><label>E manual</label><input id="asme-manual-e" inputmode="decimal" value="${this.esc(this.asme.manualQualityFactor)}" /></div>` : ""}
                ${!normative?.weldJointFactor ? `<div class="fld"><label>W manual</label><input id="asme-manual-w" inputmode="decimal" value="${this.esc(this.asme.manualWeldFactor)}" /></div>` : ""}
                ${!normative?.yCoefficient ? `<div class="fld"><label>Y manual</label><input id="asme-manual-y" inputmode="decimal" value="${this.esc(this.asme.manualYCoefficient)}" /></div>` : ""}
                <div class="fld fld-wide"><label>Justificativa técnica *</label><input id="asme-manual-just" value="${this.esc(this.asme.manualJustification)}" placeholder="Base técnica do valor informado" /></div>
              </div>
            </div>` : ""}

          <div class="calc-grid" style="margin-top:14px;">
            <div class="fld fld-wide"><label>Observações</label><input id="asme-notes" value="${this.esc(this.asme.notes)}" /></div>
          </div>

          ${this.errors.ASME_B313 ? this.message(this.errors.ASME_B313) : ""}

          <div class="calc-actions">
            <button type="button" id="btn-asme-calc" class="btn-calc">CALCULAR ESPESSURA MÍNIMA</button>
            <button type="button" id="btn-asme-save" class="btn-secondary" ${r ? "" : "disabled"}>SALVAR CÁLCULO</button>
          </div>

          ${r ? `
            <div class="result-box">
              <div class="result-head">
                <span>Resultado — ASME B31.3</span>
                <span class="result-code">${this.asmeResult?.code ? this.esc(this.asmeResult.code) : "não salvo"}</span>
              </div>
              <div class="result-grid">
                ${this.simpleRes("TAG", design.tag)}
                ${this.simpleRes("Material", this.lineContext?.matchedMaterial ? formatMaterialLabel(this.lineContext.matchedMaterial) : (design.material || "—"))}
                ${this.simpleRes("Diâmetro externo", fmtThk(design.outsideDiameterMm.value))}
                ${this.simpleRes("Pressão", fmt(usedPressure, 2, "MPa"))}
                ${this.simpleRes("Temperatura", fmt(usedTemp, 2, "°C"))}
                ${this.simpleRes("Tensão admissível", fmt(this.resolveAsmeFactors().S.value, 2, "MPa"))}
                ${this.simpleRes("Espessura por pressão", fmtThk(r.pressureThickness))}
                ${this.simpleRes("Allowance total", fmtThk(r.totalAllowance))}
                ${this.simpleRes("Espessura mínima requerida", fmtThk(r.minimumRequiredThickness), true)}
                ${this.simpleRes("Espessura nominal requerida", fmtThk(r.requiredNominalThickness))}
              </div>
              ${r.warnings.length ? `<ul class="warn-list">${r.warnings.map(w => `<li>${this.esc(w)}</li>`).join("")}</ul>` : ""}
              ${this.asmeResult?.code ? `<div class="calc-boundary">Cálculo salvo e vinculado à TAG ${this.esc(design.tag)} — o API 570 pode reaproveitá-lo como espessura mínima requerida.</div>` : ""}
            </div>` : ""}
        ` : (this.errors.ASME_B313 ? this.message(this.errors.ASME_B313) : "")}

        ${this.renderHistory("ASME_B313", r => [fmtThk((r.result as any)?.minimumRequiredThickness ?? null)], ["Espessura mínima"])}
      </div>`;
  }

  // =========================================================================
  // API 570
  // =========================================================================

  private captureApi() {
    this.api = {
      ...this.api,
      rateBasis: (this.val("api-basis") || this.api.rateBasis) as Api570RateBasis,
      manualRate: this.val("api-manual-rate"),
      rateJustification: this.val("api-rate-just"),
      manualMinimumThickness: this.val("api-manual-min"),
      minimumJustification: this.val("api-min-just"),
      notes: this.val("api-notes")
    };
  }

  private async reloadApiLine() {
    this.errors.API570 = null;
    this.pointContext = null;
    this.api.cmlId = "";
    this.cmls = [];
    if (!this.api.lineId) { this.render(); return; }

    this.loading = true;
    this.render();
    try {
      this.cmls = await this.service.listMeasurementPoints(this.ctx, this.api.lineId);
    } catch (e: any) {
      console.error("[CÁLCULOS][API 570] Falha ao listar pontos", e);
      this.errors.API570 = e?.safeMessage || e?.message || "Não foi possível listar os pontos medidos.";
    }
    this.loading = false;
    this.render();
  }

  private async reloadApiPoint() {
    this.errors.API570 = null;
    if (!this.api.cmlId) { this.pointContext = null; this.render(); return; }

    this.loading = true;
    this.render();
    try {
      this.pointContext = await this.service.loadPointContext(this.ctx, this.api.lineId, this.api.cmlId);
      this.apiResult = null;
      this.recomputeApiPreview();
    } catch (e: any) {
      console.error("[CÁLCULOS][API 570] Falha ao carregar histórico do ponto", e);
      this.errors.API570 = e?.safeMessage || e?.message || "Não foi possível carregar o histórico do ponto.";
      this.pointContext = null;
    }
    this.loading = false;
    this.render();
  }

  /** Espessura mínima requerida: primeiro o último ASME da TAG, depois entrada manual justificada. */
  private resolveApiMinimum(): { value: number | null; origin: string; manual: boolean } {
    const fromAsme = this.pointContext?.minimumThicknessFromAsme ?? null;
    if (fromAsme) {
      return { value: fromAsme.value, origin: `Cálculo ASME B31.3 nº ${fromAsme.sourceCode}`, manual: false };
    }
    const manual = parseDecimalBR(this.api.manualMinimumThickness);
    return { value: manual, origin: "Entrada técnica manual", manual: true };
  }

  private buildApiInputs() {
    const h = this.pointContext!.history;
    const minimum = this.resolveApiMinimum();
    return {
      initialThicknessMm: h.initial?.thicknessMm ?? null,
      initialDate: h.initial?.date ?? "",
      previousThicknessMm: h.previous?.thicknessMm ?? null,
      previousDate: h.previous?.date ?? "",
      currentThicknessMm: h.current?.thicknessMm ?? null,
      currentDate: h.current?.date ?? "",
      minimumRequiredThicknessMm: minimum.value,
      rateBasis: this.api.rateBasis,
      manualRateMmPerYear: parseDecimalBR(this.api.manualRate),
      rateBasisJustification: this.api.rateJustification
    };
  }

  /** ST, LT e vida remanescente são resultado — aparecem sozinhos, sem botão. */
  private recomputeApiPreview() {
    if (!this.pointContext?.history.current) { this.apiResult = null; return; }
    try {
      this.apiResult = { result: this.service.previewApi570(this.ctx, this.buildApiInputs()), code: this.apiResult?.code ?? null };
    } catch (e) {
      console.error("[CÁLCULOS][API 570] Falha na prévia", e);
      this.apiResult = null;
    }
  }

  private async saveApiAnalysis() {
    this.captureApi();
    this.errors.API570 = null;

    if (!this.pointContext?.history.current) {
      this.errors.API570 = "Selecione uma TAG e um ponto com medição registrada.";
      this.render();
      return;
    }

    const minimum = this.resolveApiMinimum();
    if (minimum.value === null) {
      this.errors.API570 = "Espessura mínima requerida não definida: rode um cálculo ASME B31.3 para a TAG ou informe o valor com justificativa.";
      this.render();
      return;
    }
    if (minimum.manual && !this.api.minimumJustification.trim()) {
      this.errors.API570 = "Espessura mínima informada manualmente: registre a justificativa técnica.";
      this.render();
      return;
    }

    try {
      const line = this.lines.find(l => l.equipmentId === this.api.lineId);
      const { result, record } = await this.service.runApi570(this.ctx, this.buildApiInputs(), {
        equipmentId: this.api.lineId,
        equipmentTag: line?.tag || "",
        notes: this.api.notes,
        provenance: {
          ponto: this.api.cmlId,
          historico: `${this.pointContext.history.readings.length} medição(ões) de ME`,
          espessuraMinima: minimum.origin,
          justificativaEspessuraMinima: minimum.manual ? (this.api.minimumJustification || "—") : "—",
          justificativaTaxa: this.api.rateJustification || "—"
        }
      });
      this.apiResult = { result, code: record.code };
      await this.reloadHistory();
    } catch (e: any) {
      console.error("[CÁLCULOS][API 570] Falha ao salvar análise", e);
      this.errors.API570 = e?.safeMessage || e?.message || "Não foi possível salvar a análise API 570.";
    }
    this.render();
  }

  private renderApiScreen(): string {
    const h = this.pointContext?.history ?? null;
    const r = this.apiResult?.result;
    const minimum = this.resolveApiMinimum();
    const fromAsme = this.pointContext?.minimumThicknessFromAsme ?? null;

    return `
      <div class="calc-panel">
        ${this.renderScreenHeader("API570", "Taxa de Corrosão e Vida Remanescente", "A partir do histórico real de medição de espessura.")}

        <div class="calc-subtitle">1 — Linha e ponto (mesmo cadastro da ASME B31.3)</div>
        ${this.renderScopeFilters("api")}
        <div class="calc-grid" style="margin-top:12px;">
          <div class="fld">
            <label>TAG da Linha *</label>
            <select id="api-line">
              <option value="">Selecionar linha cadastrada…</option>
              ${this.lines.map(l => `<option value="${this.esc(l.equipmentId)}" ${this.api.lineId === l.equipmentId ? "selected" : ""}>${this.esc(this.lineLabel(l))}</option>`).join("")}
            </select>
            ${this.renderLinesNotice()}
          </div>
          <div class="fld">
            <label>Ponto / CML *</label>
            <select id="api-cml" ${this.cmls.length === 0 ? "disabled" : ""}>
              <option value="">${this.cmls.length === 0 ? "Nenhum ponto medido para esta TAG" : "Selecionar…"}</option>
              ${this.cmls.map(c => `<option value="${this.esc(c.id)}" ${this.api.cmlId === c.id ? "selected" : ""}>${this.esc(c.id)}${c.component ? ` — ${this.esc(c.component)}` : ""} (${c.readingCount} medição(ões))</option>`).join("")}
            </select>
          </div>
        </div>

        ${this.loading ? `<div class="loading">Carregando histórico…</div>` : ""}

        ${h ? `
          <div class="calc-subtitle">2 — Histórico de medição (relatórios de ME)</div>
          ${h.readings.length === 0 ? `<div class="history-empty">Nenhuma medição registrada para este ponto.</div>` : `
            <table class="history-table">
              <thead><tr><th>Data</th><th>Espessura</th><th>Relatório</th><th>Posição</th></tr></thead>
              <tbody>
                ${h.readings.map(reading => {
                  const isCurrent = reading === h.current;
                  const isPrevious = reading === h.previous;
                  const isInitial = reading === h.initial;
                  const tag = isCurrent ? "ATUAL" : isPrevious ? "ANTERIOR" : isInitial ? "INICIAL" : "";
                  return `<tr>
                    <td>${this.esc(fmtCivilDate(reading.date))}</td>
                    <td>${fmtThk(reading.thicknessMm)}</td>
                    <td>${this.esc(reading.reportNumber || "—")}</td>
                    <td>${tag ? `<span class="pos-badge">${tag}</span>` : ""}</td>
                  </tr>`;
                }).join("")}
              </tbody>
            </table>`}

          ${!h.hasSufficientHistory ? this.message(INSUFFICIENT_HISTORY_MESSAGE) : ""}

          <div class="calc-subtitle">3 — Espessura mínima requerida</div>
          <div class="result-grid">
            <div class="res ${fromAsme ? "" : "res-missing"}">
              <span>t requerida</span>
              <strong>${fmtThk(minimum.value)}</strong>
              <small>${this.esc(minimum.origin)}${fromAsme ? ` · ${this.esc(fmtTimestamp(fromAsme.calculatedAt))}` : ""}</small>
            </div>
            ${h.registeredMinimumThicknessMm !== null ? `
              <div class="res">
                <span>Mínima registrada no relatório de ME</span>
                <strong>${fmtThk(h.registeredMinimumThicknessMm)}</strong>
                <small>Referência do relatório — não substitui o cálculo ASME</small>
              </div>` : ""}
          </div>
          ${!fromAsme ? `
            <div class="calc-grid" style="margin-top:12px;">
              <div class="fld"><label>t requerida manual (mm)</label><input id="api-manual-min" inputmode="decimal" value="${this.esc(this.api.manualMinimumThickness)}" placeholder="0,00" /></div>
              <div class="fld fld-wide"><label>Justificativa técnica *</label><input id="api-min-just" value="${this.esc(this.api.minimumJustification)}" placeholder="Base técnica da espessura mínima informada" /></div>
            </div>` : ""}

          <div class="calc-subtitle">4 — Taxa de corrosão (calculada automaticamente)</div>
          <div class="result-grid">
            ${this.simpleRes("Curto prazo (ST)", fmtRate(r?.shortTermRateMmPerYear ?? null))}
            ${this.simpleRes("Longo prazo (LT)", fmtRate(r?.longTermRateMmPerYear ?? null))}
            ${this.simpleRes("Referência conservadora", fmtRate(r?.conservativeRateMmPerYear ?? null))}
            ${this.simpleRes("Taxa adotada", fmtRate(r?.adoptedRateMmPerYear ?? null), true)}
          </div>
          <div class="calc-grid" style="margin-top:12px;">
            <div class="fld">
              <label>Taxa adotada</label>
              <select id="api-basis">
                ${(Object.keys(RATE_BASIS_LABELS) as Api570RateBasis[])
                  .map(b => `<option value="${b}" ${this.api.rateBasis === b ? "selected" : ""}>${this.esc(RATE_BASIS_LABELS[b])}</option>`).join("")}
              </select>
            </div>
            ${this.api.rateBasis === "MANUAL" ? `<div class="fld"><label>Taxa (mm/ano)</label><input id="api-manual-rate" inputmode="decimal" value="${this.esc(this.api.manualRate)}" placeholder="0,0000" /></div>` : ""}
            <div class="fld fld-wide">
              <label>Justificativa da taxa adotada ${r?.departsFromConservativeReference ? "*" : ""}</label>
              <input id="api-rate-just" value="${this.esc(this.api.rateJustification)}" placeholder="Obrigatória quando a taxa adotada não é a referência conservadora" />
            </div>
          </div>

          <div class="calc-subtitle">5 — Vida remanescente</div>
          <div class="result-grid">
            ${this.simpleRes("Espessura atual", fmtThk(h.current?.thicknessMm ?? null))}
            ${this.simpleRes("Vida remanescente", fmtYears(r?.remainingLifeYears ?? null), true)}
            ${this.simpleRes("Data projetada", fmtCivilDate(r?.estimatedDateAtMinimumThickness ?? null))}
          </div>
          ${r && r.warnings.length ? `<ul class="warn-list">${r.warnings.map(w => `<li>${this.esc(w)}</li>`).join("")}</ul>` : ""}

          <div class="calc-grid" style="margin-top:14px;">
            <div class="fld fld-wide"><label>Observações</label><input id="api-notes" value="${this.esc(this.api.notes)}" /></div>
          </div>

          ${this.errors.API570 ? this.message(this.errors.API570) : ""}

          <div class="calc-actions">
            <button type="button" id="btn-api-save" class="btn-calc">SALVAR ANÁLISE API 570</button>
            ${this.apiResult?.code ? `<span class="saved-code">Salvo como ${this.esc(this.apiResult.code)}</span>` : ""}
          </div>
        ` : (this.errors.API570 ? this.message(this.errors.API570) : "")}

        ${this.renderHistory("API570", rec => [
          fmtRate((rec.result as any)?.adoptedRateMmPerYear ?? null),
          fmtYears((rec.result as any)?.remainingLifeYears ?? null)
        ], ["Taxa adotada", "Vida remanescente"])}
      </div>`;
  }

  // =========================================================================
  // NACE / AMPP SP0775
  // =========================================================================

  private captureNace() {
    this.nace = {
      ...this.nace,
      couponMaterialId: this.val("nace-material") || this.nace.couponMaterialId,
      length: this.val("nace-length"),
      width: this.val("nace-width"),
      thickness: this.val("nace-thickness"),
      holeDiameter: this.val("nace-hole"),
      maskedArea: this.val("nace-masked"),
      areaOverride: this.val("nace-area-override"),
      areaJustification: this.val("nace-area-just"),
      initialMass: this.val("nace-initial-mass"),
      finalMass: this.val("nace-final-mass"),
      installationDate: this.val("nace-install"),
      removalDate: this.val("nace-removal"),
      equipmentTag: this.val("nace-tag"),
      notes: this.val("nace-notes")
    };
  }

  private naceDensity(): number | null {
    const material = this.couponMaterials.find(m => m.id === this.nace.couponMaterialId);
    return material ? material.densityGramsPerCm3 : null;
  }

  private buildNaceInputs() {
    const material = this.couponMaterials.find(m => m.id === this.nace.couponMaterialId);
    return {
      couponMaterialId: this.nace.couponMaterialId || null,
      materialName: material?.name ?? "",
      densityGramsPerCm3: this.naceDensity(),
      geometry: {
        lengthMm: parseDecimalBR(this.nace.length),
        widthMm: parseDecimalBR(this.nace.width),
        thicknessMm: parseDecimalBR(this.nace.thickness),
        holeDiameterMm: parseDecimalBR(this.nace.holeDiameter),
        maskedAreaMm2: parseDecimalBR(this.nace.maskedArea)
      },
      exposedAreaOverrideMm2: parseDecimalBR(this.nace.areaOverride),
      areaOverrideJustification: this.nace.areaJustification,
      initialMassGrams: parseDecimalBR(this.nace.initialMass),
      finalMassGrams: parseDecimalBR(this.nace.finalMass),
      installationDate: this.nace.installationDate,
      removalDate: this.nace.removalDate
    };
  }

  /** Área, tempo, perda de massa e taxa se atualizam sozinhos enquanto o usuário digita. */
  private recomputeNacePreview() {
    try {
      this.naceResult = { result: this.service.previewNace(this.ctx, this.buildNaceInputs()), code: this.naceResult?.code ?? null };
    } catch (e) {
      console.error("[CÁLCULOS][NACE] Falha na prévia", e);
      this.naceResult = null;
    }
  }

  private async saveNace() {
    this.captureNace();
    this.errors.NACE = null;
    try {
      const { result, record } = await this.service.runNace(this.ctx, this.buildNaceInputs(), {
        equipmentTag: this.nace.equipmentTag,
        notes: this.nace.notes,
        provenance: {
          densidade: this.nace.couponMaterialId ? "Catálogo de materiais de cupom" : "Não definida",
          area: parseDecimalBR(this.nace.areaOverride) ? "Ajuste técnico manual" : "Calculada pela geometria do cupom",
          tempo: "Calculado pelas datas de instalação e retirada"
        }
      });
      this.naceResult = { result, code: record.code };
      await this.reloadHistory();
    } catch (e: any) {
      console.error("[CÁLCULOS][NACE] Falha ao salvar", e);
      this.errors.NACE = e?.safeMessage || e?.message || "Não foi possível salvar o cálculo NACE / AMPP.";
    }
    this.render();
  }

  private renderNaceScreen(): string {
    const r = this.naceResult?.result;
    const density = this.naceDensity();

    return `
      <div class="calc-panel">
        ${this.renderScreenHeader("NACE", "Cupom de Corrosão", "Taxa média de corrosão pela perda de massa do cupom.")}

        <div class="calc-subtitle">1 — Cupom</div>
        <div class="calc-grid">
          <div class="fld">
            <label>Material do cupom *</label>
            <select id="nace-material">
              <option value="">Selecionar…</option>
              ${this.couponMaterials.map(m => `<option value="${this.esc(m.id)}" ${this.nace.couponMaterialId === m.id ? "selected" : ""}>${this.esc(m.name)}</option>`).join("")}
            </select>
            ${density !== null ? `<span class="fld-trace">Densidade: ${fmt(density, 2, "g/cm³")} — do catálogo de materiais.</span>` : `<span class="fld-hint">A densidade vem do material selecionado.</span>`}
          </div>
          <div class="fld"><label>TAG / Ponto de instalação</label><input id="nace-tag" value="${this.esc(this.nace.equipmentTag)}" /></div>
        </div>

        <div class="calc-subtitle">2 — Dimensões (mm)</div>
        <div class="calc-grid">
          <div class="fld"><label>Comprimento (mm)</label><input id="nace-length" class="nace-input" inputmode="decimal" value="${this.esc(this.nace.length)}" placeholder="0,00" /></div>
          <div class="fld"><label>Largura (mm)</label><input id="nace-width" class="nace-input" inputmode="decimal" value="${this.esc(this.nace.width)}" placeholder="0,00" /></div>
          <div class="fld"><label>Espessura (mm)</label><input id="nace-thickness" class="nace-input" inputmode="decimal" value="${this.esc(this.nace.thickness)}" placeholder="0,00" /></div>
          <div class="fld"><label>Diâmetro do furo (mm)</label><input id="nace-hole" class="nace-input" inputmode="decimal" value="${this.esc(this.nace.holeDiameter)}" placeholder="0,00" /></div>
          <div class="fld"><label>Área mascarada / protegida (mm²)</label><input id="nace-masked" class="nace-input" inputmode="decimal" value="${this.esc(this.nace.maskedArea)}" placeholder="0,00" /></div>
          <div class="fld">
            <label>Ajuste técnico da área exposta (mm²)</label>
            <input id="nace-area-override" class="nace-input" inputmode="decimal" value="${this.esc(this.nace.areaOverride)}" placeholder="opcional" />
            <span class="fld-hint">Só quando a geometria não representar a área exposta real.</span>
          </div>
          ${parseDecimalBR(this.nace.areaOverride) !== null ? `<div class="fld fld-wide"><label>Justificativa do ajuste *</label><input id="nace-area-just" class="nace-input" value="${this.esc(this.nace.areaJustification)}" /></div>` : ""}
        </div>

        <div class="calc-subtitle">3 — Massa e exposição</div>
        <div class="calc-grid">
          <div class="fld"><label>Massa inicial (g)</label><input id="nace-initial-mass" class="nace-input" inputmode="decimal" value="${this.esc(this.nace.initialMass)}" placeholder="0,0000" /></div>
          <div class="fld"><label>Massa final (g)</label><input id="nace-final-mass" class="nace-input" inputmode="decimal" value="${this.esc(this.nace.finalMass)}" placeholder="0,0000" /></div>
          <div class="fld"><label>Data de instalação</label><input id="nace-install" class="nace-input" type="date" value="${this.esc(this.nace.installationDate)}" /></div>
          <div class="fld"><label>Data de retirada</label><input id="nace-removal" class="nace-input" type="date" value="${this.esc(this.nace.removalDate)}" /></div>
          <div class="fld fld-wide"><label>Observações</label><input id="nace-notes" value="${this.esc(this.nace.notes)}" /></div>
        </div>

        <div class="calc-subtitle">4 — Calculado pelo sistema</div>
        <div class="result-grid">
          ${this.simpleRes("Área exposta", fmtArea(r?.exposedAreaMm2 ?? null))}
          ${this.simpleRes("Perda de massa", fmtMass(r?.massLossGrams ?? null))}
          ${this.simpleRes("Exposição", r?.exposureDays !== null && r?.exposureDays !== undefined ? `${formatDecimalBR(r.exposureDays, 0)} dias` : "—")}
          ${this.simpleRes("Densidade", fmt(r?.densityGramsPerCm3 ?? null, 2, "g/cm³"))}
          ${this.simpleRes("Taxa de corrosão", fmtRate(r?.corrosionRateMmPerYear ?? null), true)}
          ${this.simpleRes("Severidade (SP0775)", r?.severity || "—")}
        </div>
        ${r && r.warnings.length ? `<ul class="warn-list">${r.warnings.map(w => `<li>${this.esc(w)}</li>`).join("")}</ul>` : ""}

        <div class="other-units">
          <button type="button" id="btn-nace-units" class="btn-link">${this.nace.showOtherUnits ? "▾" : "▸"} DETALHES / OUTRAS UNIDADES</button>
          ${this.nace.showOtherUnits ? `
            <div class="result-grid" style="margin-top:10px;">
              ${this.simpleRes("Taxa de corrosão", fmt(r?.corrosionRateMpy ?? null, 2, "mpy"))}
              ${this.simpleRes("Faces principais", fmtArea(r?.area.faceAreaMm2 ?? null))}
              ${this.simpleRes("Bordas", fmtArea(r?.area.edgeAreaMm2 ?? null))}
              ${this.simpleRes("Desconto do furo", fmtArea(r?.area.holeDeductionMm2 ?? null))}
              ${this.simpleRes("Parede do furo", fmtArea(r?.area.holeWallAreaMm2 ?? null))}
              ${this.simpleRes("Origem da área", r?.areaOrigin === "MANUAL" ? "Ajuste técnico manual" : "Geometria do cupom")}
            </div>` : ""}
        </div>

        ${this.errors.NACE ? this.message(this.errors.NACE) : ""}

        <div class="calc-actions">
          <button type="button" id="btn-nace-save" class="btn-calc">SALVAR CÁLCULO NACE / AMPP</button>
          ${this.naceResult?.code ? `<span class="saved-code">Salvo como ${this.esc(this.naceResult.code)}</span>` : ""}
        </div>
        <div class="calc-boundary">Resultado exclusivo do cupom. Não é transferido para o cálculo API 570.</div>

        ${this.renderHistory("NACE", rec => [
          fmtRate((rec.result as any)?.corrosionRateMmPerYear ?? null),
          this.esc((rec.result as any)?.severity || "—")
        ], ["Taxa de corrosão", "Severidade"])}
      </div>`;
  }

  // =========================================================================
  // Blocos comuns
  // =========================================================================

  /**
   * A TAG aparece na lista mesmo faltando propriedade técnica; o que falta é
   * apontado aqui, depois da seleção, em vez de a linha sumir do seletor.
   */
  private renderRegistryGaps(design: any, matchedMaterial: PipingMaterial | null): string {
    const gaps: string[] = [];
    if (design.outsideDiameterMm.value === null) gaps.push("Diâmetro externo não cadastrado (informe o OD ou o NPS/DN da linha).");
    if (!design.material && !matchedMaterial) gaps.push("Material não definido no cadastro da linha.");
    if (design.designPressureMPa.value === null) gaps.push("Pressão de projeto não cadastrada.");
    if (design.designTemperatureCelsius.value === null) gaps.push("Temperatura de projeto não cadastrada.");
    if (design.nominalThicknessMm.value === null) gaps.push("Espessura nominal não cadastrada.");

    if (gaps.length === 0) return "";
    return `
      <ul class="warn-list" style="margin-top:12px;">
        ${gaps.map(gap => `<li>⚠ ${this.esc(gap)}</li>`).join("")}
      </ul>`;
  }

  /** Rótulo do seletor: TAG — descrição/serviço, quando houver. */
  private lineLabel(line: CalculableLine): string {
    const parts = [line.tag];
    const detail = line.service || line.name;
    if (detail && detail !== line.tag) parts.push(detail);
    if (line.nps) parts.push(`NPS ${line.nps}"`);
    return parts.join(" — ");
  }

  /**
   * Cliente e Projeto são filtros do seletor de TAG, comuns às duas
   * calculadoras. Sem nenhum escolhido, todas as linhas cadastradas aparecem.
   */
  private renderScopeFilters(prefix: string): string {
    const projects = this.scope.clientId
      ? this.projects.filter(p => p.clientId === this.scope.clientId)
      : this.projects;

    return `
      <div class="calc-grid">
        <div class="fld">
          <label>Cliente</label>
          <select id="${prefix}-client">
            <option value="">Todos os clientes</option>
            ${this.clients.map(c => `<option value="${this.esc(c.id)}" ${this.scope.clientId === c.id ? "selected" : ""}>${this.esc(c.name)}</option>`).join("")}
          </select>
        </div>
        <div class="fld">
          <label>Projeto</label>
          <select id="${prefix}-project">
            <option value="">Todos os projetos</option>
            ${projects.map(p => `<option value="${this.esc(p.id)}" ${this.scope.projectId === p.id ? "selected" : ""}>${this.esc(p.code)} — ${this.esc(p.name)}</option>`).join("")}
          </select>
        </div>
      </div>`;
  }

  /** Mensagem específica quando o seletor de TAG não tem o que oferecer. */
  private renderLinesNotice(): string {
    if (this.linesError) return this.message(this.linesError);
    if (this.lines.length > 0) return "";
    const scoped = this.scope.clientId || this.scope.projectId;
    return `<div class="fld-hint" style="margin-top:6px;">
      ${scoped
        ? "Nenhuma tubulação cadastrada para o Cliente/Projeto selecionados. Limpe os filtros para ver todas."
        : "Nenhuma tubulação cadastrada. Cadastre a linha em Equipamentos &amp; Ativos → Equipamento / Tubulação."}
    </div>`;
  }

  private registryField(label: string, value: string, source?: string): string {
    return `
      <div class="reg">
        <span>${this.esc(label)}</span>
        <strong>${this.esc(value)}</strong>
        ${source ? `<small>${this.esc(source)}</small>` : ""}
      </div>`;
  }

  private simpleRes(label: string, value: string, main = false): string {
    return `
      <div class="res ${main ? "res-main" : ""}">
        <span>${this.esc(label)}</span>
        <strong>${value}</strong>
      </div>`;
  }

  private renderScreenHeader(kind: CalculationKind, title: string, subtitle: string): string {
    const norm = CALCULATION_NORMS[kind];
    const edition = kind === "ASME_B313"
      ? this.asmeEditions.find(e => e.id === this.asme.editionId)
      : null;
    return `
      <div class="calc-ident">
        <div>
          <button type="button" class="btn-link btn-back">← Cálculos de Integridade</button>
          <h2>${this.esc(TAB_SHORT_LABELS[kind])} — ${this.esc(title)}</h2>
          <p>${this.esc(subtitle)}</p>
        </div>
        <div class="norm-chip">
          <span>NORMA</span>
          <strong>${this.esc(norm.norm)}</strong>
          <small>EDIÇÃO: ${this.esc(edition ? edition.edition : norm.edition)}</small>
        </div>
      </div>`;
  }

  private renderHistory(
    kind: CalculationKind,
    columns: (record: CalculationRecord) => string[],
    headers: string[]
  ): string {
    const records = this.history[kind];
    return `
      <div class="history-box">
        <div class="history-head">Histórico — ${this.esc(TAB_SHORT_LABELS[kind])}</div>
        ${records.length === 0 ? `<div class="history-empty">Nenhum cálculo ${this.esc(TAB_SHORT_LABELS[kind])} registrado.</div>` : `
          <table class="history-table">
            <thead>
              <tr>
                <th>Código</th><th>Data</th><th>TAG</th>
                ${headers.map(h => `<th>${this.esc(h)}</th>`).join("")}
                <th>Norma / Edição</th>
              </tr>
            </thead>
            <tbody>
              ${records.map(rec => `
                <tr>
                  <td class="mono">${this.esc(rec.code)}</td>
                  <td>${this.esc(fmtTimestamp(rec.performedAt))}</td>
                  <td>${this.esc(rec.equipmentTag || "—")}</td>
                  ${columns(rec).map(c => `<td>${c}</td>`).join("")}
                  <td>${this.esc(rec.norm)} · ${this.esc(rec.normEdition)}</td>
                </tr>`).join("")}
            </tbody>
          </table>`}
      </div>`;
  }

  // =========================================================================

  render() {
    if (!this.service) {
      this.innerHTML = `<div style="padding:24px; color:var(--color-text-muted);">Serviço de cálculos indisponível nesta sessão.</div>`;
      return;
    }

    this.innerHTML = `
      <style>
        .calc-container { padding: 24px; font-family: Inter, sans-serif; }
        .calc-header { margin-bottom: 20px; border-bottom: 1px solid var(--color-border); padding-bottom: 12px; }
        .calc-header h1 { margin: 0; font-size: 1.5rem; color: var(--ia-primary); }
        .calc-header p { margin: 4px 0 0 0; font-size: 0.85rem; color: var(--color-text-muted); }
        .hub-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 16px; }
        .hub-card { background: var(--color-surface); border: 1px solid var(--color-border); border-radius: 10px; padding: 20px; display: flex; flex-direction: column; }
        .hub-norm { font-size: 0.68rem; letter-spacing: 0.1em; color: var(--color-text-muted); }
        .hub-card h2 { margin: 6px 0 0 0; font-size: 1.2rem; color: var(--ia-primary); }
        .hub-card p { margin: 6px 0 0 0; font-size: 0.85rem; color: var(--color-text-primary); flex: 1; }
        .hub-count { font-size: 0.74rem; color: var(--color-text-muted); margin: 14px 0; }
        .calc-panel { background: var(--color-surface); border: 1px solid var(--color-border); border-radius: 10px; padding: 20px; }
        .calc-ident { display: flex; justify-content: space-between; align-items: flex-start; gap: 18px; flex-wrap: wrap; margin-bottom: 8px; }
        .calc-ident h2 { margin: 8px 0 0 0; font-size: 1.15rem; color: var(--color-text-primary); }
        .calc-ident p { margin: 4px 0 0 0; font-size: 0.82rem; color: var(--color-text-muted); }
        .norm-chip { border: 1px solid var(--color-border); border-radius: 8px; padding: 8px 14px; min-width: 200px; }
        .norm-chip span { display: block; font-size: 0.66rem; letter-spacing: 0.08em; color: var(--color-text-muted); }
        .norm-chip strong { display: block; font-size: 0.95rem; color: var(--ia-primary); }
        .norm-chip small { display: block; font-size: 0.68rem; color: var(--color-text-muted); margin-top: 2px; }
        .calc-subtitle { font-size: 0.72rem; letter-spacing: 0.09em; font-weight: 700; color: var(--ia-primary); margin: 20px 0 8px 0; }
        .calc-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; }
        .registry-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 10px; }
        .reg { border: 1px solid var(--color-border); border-radius: 8px; padding: 8px 10px; background: rgba(0,0,0,0.12); }
        .reg span { display: block; font-size: 0.68rem; color: var(--color-text-muted); }
        .reg strong { display: block; font-size: 0.9rem; color: var(--color-text-primary); margin-top: 3px; }
        .reg small { display: block; font-size: 0.64rem; color: var(--color-text-muted); margin-top: 3px; }
        .fld { display: flex; flex-direction: column; }
        .fld-wide { grid-column: span 2; }
        .fld label { font-size: 0.72rem; color: var(--color-text-muted); font-weight: 600; margin-bottom: 4px; }
        .fld input, .fld select { width: 100%; padding: 8px 10px; background: rgba(0,0,0,0.25); border: 1px solid var(--color-graphite-700, var(--color-border)); border-radius: 6px; color: var(--color-text-primary); box-sizing: border-box; font-size: 0.86rem; }
        .fld-hint { font-size: 0.7rem; color: var(--color-text-muted); margin-top: 4px; }
        .fld-trace { font-size: 0.7rem; color: var(--ia-primary); margin-top: 4px; }
        .fld-scenario { font-size: 0.7rem; color: var(--ia-warning-fg, #ffcc80); margin-top: 4px; }
        .manual-box { margin-top: 14px; border: 1px dashed var(--color-border); border-radius: 8px; padding: 14px; }
        .manual-title { font-size: 0.72rem; font-weight: 700; color: var(--ia-warning-fg, #ffcc80); margin-bottom: 10px; }
        .calc-actions { margin-top: 20px; display: flex; gap: 12px; align-items: center; flex-wrap: wrap; }
        .btn-calc { padding: 10px 22px; background: var(--ia-action-bg, var(--color-primary)); color: var(--ia-action-fg, var(--color-text-primary)); border: none; border-radius: 6px; cursor: pointer; font-weight: 700; font-size: 0.86rem; }
        .btn-secondary { padding: 10px 22px; background: none; border: 1px solid var(--color-primary); color: var(--ia-primary); border-radius: 6px; cursor: pointer; font-weight: 700; font-size: 0.86rem; }
        .btn-secondary:disabled { opacity: 0.4; cursor: not-allowed; border-color: var(--color-border); color: var(--color-text-muted); }
        .saved-code { font-size: 0.78rem; color: var(--color-text-muted); font-family: monospace; }
        .calc-error { margin-top: 16px; padding: 10px 12px; border-radius: 6px; background: #b71c1c; color: #ffebee; font-size: 0.82rem; }
        .loading { padding: 16px; color: var(--color-text-muted); font-size: 0.84rem; }
        .result-box { margin-top: 22px; border: 1px solid var(--color-primary); border-radius: 10px; padding: 16px; }
        .result-head { display: flex; justify-content: space-between; align-items: center; font-size: 0.78rem; letter-spacing: 0.08em; font-weight: 700; color: var(--ia-primary); margin-bottom: 12px; }
        .result-code { font-family: monospace; color: var(--color-text-muted); letter-spacing: 0; }
        .result-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; }
        .res { border: 1px solid var(--color-border); border-radius: 8px; padding: 10px 12px; }
        .res span { display: block; font-size: 0.72rem; color: var(--color-text-muted); }
        .res strong { display: block; font-size: 1.05rem; color: var(--color-text-primary); margin-top: 4px; }
        .res small { display: block; font-size: 0.66rem; color: var(--color-text-muted); margin-top: 5px; line-height: 1.4; }
        .res small .pending { display: block; color: var(--ia-warning-fg, #ffcc80); margin-top: 3px; letter-spacing: 0.04em; }
        .res-main { border-color: var(--color-primary); }
        .res-main strong { color: var(--ia-primary); }
        .res-missing { border-color: var(--status-danger-solid, #b71c1c); }
        .warn-list { margin: 12px 0 0 0; padding-left: 18px; font-size: 0.78rem; color: var(--ia-warning-fg, #ffcc80); }
        .calc-boundary { margin-top: 12px; font-size: 0.74rem; color: var(--color-text-muted); font-style: italic; }
        .other-units { margin-top: 16px; }
        .history-box { margin-top: 26px; }
        .history-head { font-size: 0.72rem; letter-spacing: 0.09em; font-weight: 700; color: var(--ia-primary); margin-bottom: 8px; }
        .history-empty { padding: 18px; text-align: center; border: 1px dashed var(--color-border); border-radius: 8px; color: var(--color-text-muted); font-size: 0.82rem; }
        .history-table { width: 100%; border-collapse: collapse; font-size: 0.8rem; }
        .history-table th, .history-table td { text-align: left; padding: 9px 10px; border-bottom: 1px solid var(--color-border); }
        .history-table th { color: var(--color-text-muted); font-weight: 600; font-size: 0.72rem; }
        .history-table .mono { font-family: monospace; }
        .pos-badge { display: inline-block; padding: 2px 7px; border-radius: 4px; font-size: 0.66rem; font-weight: 700; background: var(--color-graphite-700, #333); color: var(--ia-primary); }
        .btn-link { background: none; border: none; color: var(--color-text-muted); cursor: pointer; font-size: 0.8rem; padding: 0; }
        .btn-link:hover { color: var(--ia-primary); text-decoration: underline; }
        @media (max-width: 1100px) { .hub-grid, .calc-grid, .result-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } .registry-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
        @media (max-width: 700px) { .hub-grid, .calc-grid, .result-grid, .registry-grid { grid-template-columns: 1fr; } .fld-wide { grid-column: span 1; } }
      </style>

      <div class="calc-container">
        <div class="calc-header">
          <h1>Cálculos de Integridade</h1>
          <p>Três cálculos independentes: cada um com a sua norma, as suas entradas, o seu resultado e o seu histórico.</p>
        </div>
        ${this.view === "HUB" ? this.renderHub()
          : this.view === "ASME_B313" ? this.renderAsmeScreen()
          : this.view === "API570" ? this.renderApiScreen()
          : this.renderNaceScreen()}
      </div>`;

    this.attachEvents();
  }

  private attachEvents() {
    this.querySelectorAll<HTMLElement>(".btn-open").forEach(btn => {
      btn.addEventListener("click", () => {
        this.view = btn.dataset["view"] as View;
        this.render();
      });
    });
    this.querySelector(".btn-back")?.addEventListener("click", () => {
      this.view = "HUB";
      this.render();
    });

    // Cliente/Projeto estreitam o seletor de TAG nas duas calculadoras.
    for (const prefix of ["asme", "api"]) {
      this.querySelector(`#${prefix}-client`)?.addEventListener("change", async e => {
        this.scope.clientId = (e.target as HTMLSelectElement).value;
        this.scope.projectId = "";
        await this.reloadLines();
        this.render();
      });
      this.querySelector(`#${prefix}-project`)?.addEventListener("change", async e => {
        this.scope.projectId = (e.target as HTMLSelectElement).value;
        await this.reloadLines();
        this.render();
      });
    }

    // --- ASME B31.3 ---
    this.querySelector("#asme-line")?.addEventListener("change", e => {
      this.captureAsme();
      this.asme.lineId = (e.target as HTMLSelectElement).value;
      this.asme.materialId = "";
      this.asme.pressure = "";
      this.asme.temperature = "";
      this.asme.corrosionAllowance = "";
      this.asmeResult = null;
      void this.reloadLineContext();
    });
    this.querySelector("#asme-edition")?.addEventListener("change", e => {
      this.captureAsme();
      this.asme.editionId = (e.target as HTMLSelectElement).value;
      void this.reloadLineContext();
    });
    this.querySelector("#asme-material")?.addEventListener("change", e => {
      this.captureAsme();
      this.asme.materialId = (e.target as HTMLSelectElement).value;
      void this.reloadLineContext();
    });
    // Mudar a temperatura busca o S correspondente.
    this.querySelector("#asme-temperature")?.addEventListener("change", () => {
      this.captureAsme();
      void this.reloadLineContext();
    });
    this.querySelector("#btn-asme-calc")?.addEventListener("click", () => void this.calculateAsme(false));
    this.querySelector("#btn-asme-save")?.addEventListener("click", () => void this.calculateAsme(true));

    // --- API 570 ---
    this.querySelector("#api-line")?.addEventListener("change", e => {
      this.api.lineId = (e.target as HTMLSelectElement).value;
      void this.reloadApiLine();
    });
    this.querySelector("#api-cml")?.addEventListener("change", e => {
      this.api.cmlId = (e.target as HTMLSelectElement).value;
      void this.reloadApiPoint();
    });
    this.querySelector("#api-basis")?.addEventListener("change", () => {
      this.captureApi();
      this.recomputeApiPreview();
      this.render();
    });
    ["#api-manual-rate", "#api-manual-min", "#api-rate-just", "#api-min-just"].forEach(sel => {
      this.querySelector(sel)?.addEventListener("change", () => {
        this.captureApi();
        this.recomputeApiPreview();
        this.render();
      });
    });
    this.querySelector("#btn-api-save")?.addEventListener("click", () => void this.saveApiAnalysis());

    // --- NACE ---
    this.querySelector("#nace-material")?.addEventListener("change", () => {
      this.captureNace();
      this.recomputeNacePreview();
      this.render();
    });
    this.querySelectorAll<HTMLInputElement>(".nace-input").forEach(input => {
      input.addEventListener("change", () => {
        this.captureNace();
        this.recomputeNacePreview();
        this.render();
      });
    });
    this.querySelector("#btn-nace-units")?.addEventListener("click", () => {
      this.captureNace();
      this.nace.showOtherUnits = !this.nace.showOtherUnits;
      this.render();
    });
    this.querySelector("#btn-nace-save")?.addEventListener("click", () => void this.saveNace());
  }
}

if (!customElements.get("app-engineering-calculations-page")) {
  customElements.define("app-engineering-calculations-page", EngineeringCalculationsPage);
}
